import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./TripAI.css";
import MultiSelectModal from "./MultiSelectModal";
import PreferencesScreen from "./PreferencesScreen";
import Common, { API_BASE_URL } from "../../Common/Common";
import { DateTime } from "luxon";
import { geocode, setKey, RequestType } from "react-geocode";
import { MAX_TRIP_DAYS, overlapError, totalDaySpan, tripLengthError } from "../../Common/DestinationValidation";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";

// "Destinations from AI" — sibling to ExploreDestinationsFlow.js, sharing the
// same "pick countries/dates/nights, review a candidate city list" shape but
// replacing the deterministic Common.orderCitiesByNearestNeighbor/
// allocateDaysByPopularity ranking with an AI call that also takes the same
// preference set TripAI.js already collects (trip type, focus, activities,
// budget, pace, transportation, proximity). The deterministic algorithm has
// no concept of trip type — testing a 2-week South Africa trip including a
// safari surfaced exactly this: it can only rank by generic popularity, with
// no way to know a safari-gateway town matters here. Added 2026-08-28 — see
// memory: project_destinations_first_redesign_idea.
//
// The review phase (reorder/edit-nights/toggle/swap/add-city) is duplicated
// from ExploreDestinationsFlow.js rather than extracted into a shared
// component — that file is a working, heavily-interdependent-state feature,
// and this codebase's own precedent (the BuildMyTripWizard/DestinationsScreen
// split) is to duplicate a cohesive block like this rather than risk a
// lift-and-extract refactor of it. Candidates end up in the exact same shape
// ({id, city, country, dateStart, dateEnd, days, selected}) either way, so
// this duplication is truly just the review UI, not a second implementation
// of anything semantically different.
//
// Props: onApply(destinations) — same shape as ExploreDestinationsFlow.js;
// onCancel().
const AIDestinationsFlow = ({ onApply, onCancel }) => {
  const GuideSettings = useSelector((state) => state.GuideSettings);
  const dayStartHour = GuideSettings?.schedule_day_start ?? 9;
  const dayEndHour = GuideSettings?.schedule_day_end ?? 20;

  const [phase, setPhase] = useState("setup"); // "setup" | "review"
  const [aiSelectedCountries, setAiSelectedCountries] = useState([]);
  const [aiStartDateTime, setAiStartDateTime] = useState(() =>
    DateTime.now().setZone("utc", { keepLocalTime: true }).plus({ days: 1 }).set({ hour: dayStartHour, minute: 0 })
  );
  const [aiEndDateTime, setAiEndDateTime] = useState(() =>
    DateTime.now().setZone("utc", { keepLocalTime: true }).plus({ days: 8 }).set({ hour: dayEndHour, minute: 0 })
  );
  const [aiStartCity, setAiStartCity] = useState(null);
  const [aiEndCity, setAiEndCity] = useState(null);
  const [aiMinNights, setAiMinNights] = useState(2);
  const [aiDatesFlexible, setAiDatesFlexible] = useState(false);
  const [aiCandidates, setAiCandidates] = useState([]);
  const [aiTripStartDate, setAiTripStartDate] = useState(null);
  const [aiTripEndDate, setAiTripEndDate] = useState(null);
  const [aiError, setAiError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [multiSelect, setMultiSelect] = useState(null); // {items, selectedItems, mode, callback, title} | null
  // Preferences live on Guide.aiPreferences now, edited independently via
  // PreferencesScreen.js (elevated the same way Destinations was) — this
  // just re-renders when it's edited so the read-only summary below stays
  // current.
  const [showPreferences, setShowPreferences] = useState(false);

  const openMultiSelect = (items, selectedItems, mode, callback, title) => {
    setMultiSelect({ items, selectedItems, mode, callback, title });
  };

  const closeAll = () => {
    setPhase("setup");
    setAiCandidates([]);
    setAiTripStartDate(null);
    setAiTripEndDate(null);
    setAiError("");
    onCancel();
  };

  const selectAiCountries = () => {
    const available = countries.filter((c) => Array.isArray(citiesByCountry[c]) && citiesByCountry[c].length > 0).sort();
    const items = available.map((c, i) => ({ label: c, name: c, value: c, id: (i + 1).toString() }));
    const selectedItems = items.filter((it) => aiSelectedCountries.includes(it.value));
    openMultiSelect(items, selectedItems, "multi", aiCountriesSelected, "Select Countries (Destinations from AI)");
  };

  const aiCountriesSelected = (selected) => {
    const nextCountries = selected.map((s) => s.value);
    setAiSelectedCountries(nextCountries);
    setAiStartCity((prev) => (nextCountries.includes(prev?.country) ? prev : null));
    setAiEndCity((prev) => (nextCountries.includes(prev?.country) ? prev : null));
  };

  const aiCityChoiceItems = () => {
    const items = [];
    let id = 1;
    aiSelectedCountries.forEach((country) => {
      (citiesByCountry[country] || []).forEach((c) => {
        items.push({ label: `${c.city}, ${country}`, name: c.city, value: `${c.city}||${country}`, id: (id++).toString() });
      });
    });
    return items;
  };

  const selectAiStartCity = () => {
    if (aiSelectedCountries.length === 0) {
      setAiError("Select countries first.");
      return;
    }
    const items = aiCityChoiceItems();
    const selectedItems = aiStartCity ? items.filter((it) => it.value === `${aiStartCity.city}||${aiStartCity.country}`) : [];
    openMultiSelect(items, selectedItems, "uni", aiStartCitySelected, "Starting City (optional — e.g. your arrival flight)");
  };

  const aiStartCitySelected = (selected) => {
    if (!selected || selected.length === 0) {
      setAiStartCity(null);
      return;
    }
    const [city, country] = selected[0].value.split("||");
    const cityData = (citiesByCountry[country] || []).find((c) => c.city === city);
    setAiStartCity(cityData ? { ...cityData, country } : null);
  };

  const selectAiEndCity = () => {
    if (aiSelectedCountries.length === 0) {
      setAiError("Select countries first.");
      return;
    }
    const items = aiCityChoiceItems();
    const selectedItems = aiEndCity ? items.filter((it) => it.value === `${aiEndCity.city}||${aiEndCity.country}`) : [];
    openMultiSelect(items, selectedItems, "uni", aiEndCitySelected, "Ending City (optional — e.g. your departure flight)");
  };

  const aiEndCitySelected = (selected) => {
    if (!selected || selected.length === 0) {
      setAiEndCity(null);
      return;
    }
    const [city, country] = selected[0].value.split("||");
    const cityData = (citiesByCountry[country] || []).find((c) => c.city === city);
    setAiEndCity(cityData ? { ...cityData, country } : null);
  };

  // ── Review-phase date/day math — verbatim from ExploreDestinationsFlow.js ─
  const rechainAiDates = (candidates, tripStart, tripEnd) => {
    if (candidates.length === 0) return candidates;
    let lastSelectedIdx = -1;
    for (let i = candidates.length - 1; i >= 0; i--) {
      if (candidates[i].selected) { lastSelectedIdx = i; break; }
    }
    let cursor = (tripStart || candidates[0].dateStart).set({ hour: dayStartHour, minute: 0 });
    let firstSelectedSeen = false;
    return candidates.map((c, i) => {
      if (!c.selected) {
        return { ...c, dateStart: cursor, dateEnd: cursor.plus({ days: c.days - 1 }).set({ hour: dayEndHour, minute: 0 }) };
      }
      const dateStart = firstSelectedSeen ? cursor : (tripStart || cursor);
      firstSelectedSeen = true;
      const dateEnd = i === lastSelectedIdx && tripEnd ? tripEnd : dateStart.plus({ days: c.days - 1 }).set({ hour: dayEndHour, minute: 0 });
      cursor = dateEnd.plus({ days: 1 }).set({ hour: dayStartHour, minute: 0 });
      return { ...c, dateStart, dateEnd };
    });
  };

  const rebalanceAiDays = (candidates, id, newDays, minNights) => {
    const idx = candidates.findIndex((c) => c.id === id);
    if (idx === -1) return null;
    const oldDays = candidates[idx].days;
    const delta = newDays - oldDays;
    if (delta === 0) return candidates;

    const next = candidates.map((c) => ({ ...c }));
    next[idx].days = newDays;

    const otherIndices = next
      .map((c, i) => i)
      .filter((i) => i !== idx && next[i].selected)
      .reverse();

    if (delta > 0) {
      let remaining = delta;
      for (const i of otherIndices) {
        if (remaining <= 0) break;
        const room = next[i].days - minNights;
        if (room <= 0) continue;
        const take = Math.min(room, remaining);
        next[i].days -= take;
        remaining -= take;
      }
      if (remaining > 0) return null;
    } else if (otherIndices.length > 0) {
      next[otherIndices[0]].days += -delta;
    } else {
      return null;
    }
    return next;
  };

  // ── Generation — AI call replaces the deterministic algorithm ────────────
  // Builds the same kind of natural-language prompt TripAI.js's formQuestion
  // does, folding in all 7 preference fields, then asks for a JSON array of
  // {city, country, nights} instead of a full attraction-by-attraction
  // schedule. Explicitly instructed to include real towns matching the trip
  // type/activities (safari gateway towns, ski resort towns, beach towns),
  // not just generically popular cities — the exact gap the deterministic
  // popularity-ranked algorithm has.
  const buildAiPrompt = (totalDays, minNights) => {
    // Preferences are edited independently now (PreferencesScreen.js) — read
    // the guide's current values at prompt-build time rather than keeping a
    // local copy that could go stale if edited via "Edit Preferences"
    // mid-session.
    const prefs = window.guide_obj?.aiPreferences || {};
    const tripType = prefs.TripType ? ` ${prefs.TripType}` : "";
    const travelingWith = prefs.travelingWith ? ` ${prefs.travelingWith}` : "";
    const budget = prefs.selectedBudget ? ` ${prefs.selectedBudget}` : "";
    const pace = prefs.selectedPace ? ` ${prefs.selectedPace}` : "";
    const transport = prefs.selectedTransportation ? ` ${prefs.selectedTransportation}` : "";
    const focusPart = prefs.focusOn ? `. Focus on ${prefs.focusOn}` : "";
    const activitiesPart = prefs.Activities ? `. Include destinations suited to the following activities: ${prefs.Activities}` : "";
    const accessibilityPart = prefs.selectedAccessibility ? `. Favor destinations accessible for a traveler with ${prefs.selectedAccessibility === "wheelchair accessible" ? "wheelchair accessibility needs" : "limited mobility"}` : "";
    const excursionsPart = prefs.includeExcursions
      ? " Also consider smaller nearby towns worth a day-trip excursion from a larger base city as their own destination entries, not just the largest/most famous city in the area."
      : "";
    // Previously only applied AFTER the LLM responded (forcing the pinned
    // city into the result list, reordering it to the front/back) — the LLM
    // never knew a fixed arrival/departure point existed while choosing and
    // routing the OTHER destinations, so it could suggest a geographic
    // pattern that didn't actually flow through them. Told explicitly here
    // instead, so the whole route gets planned around the real constraint.
    // Found 2026-08-28.
    let startEndPart = "";
    if (aiStartCity && aiEndCity) {
      startEndPart = ` The trip must start in ${aiStartCity.city}, ${aiStartCity.country} and end in ${aiEndCity.city}, ${aiEndCity.country} (real flight arrival/departure points) — include both as destination entries in your response, and order every other destination to form a sensible route between them.`;
    } else if (aiStartCity) {
      startEndPart = ` The trip must start in ${aiStartCity.city}, ${aiStartCity.country} (a real flight arrival point) — include it as a destination entry in your response, and order every other destination to form a sensible route from there.`;
    } else if (aiEndCity) {
      startEndPart = ` The trip must end in ${aiEndCity.city}, ${aiEndCity.country} (a real flight departure point) — include it as a destination entry in your response, and order every other destination to form a sensible route ending there.`;
    }
    const countryList = aiSelectedCountries.join(" and ");

    const format = `Respond with ONLY RFC8259 compliant JSON, no markdown, no commentary — an array of objects in this format:
[{
  "city": "the city or town name",
  "country": "the country name — must be one of: ${aiSelectedCountries.join(", ")}",
  "nights": integer number of nights to spend there
}]
The nights must sum to exactly ${totalDays}. Prefer real, well-known cities and towns, including smaller towns when relevant to the trip type or activities (e.g. a safari-gateway town for a safari trip, a ski-resort town for a skiing trip, a coastal town for a beach trip) — do not limit suggestions to only the most famous/generic tourist cities when a smaller, more specific place better fits the trip.${excursionsPart}${startEndPart} Order the destinations in a sensible geographic travel sequence. Aim for at least ${minNights} night(s) per stop unless fewer, longer stops better fit the trip length.`;

    return `Suggest destinations for a ${totalDays} day${tripType}${budget}${pace} trip${transport}${travelingWith} ${countryList}${focusPart}${activitiesPart}${accessibilityPart}. ${format}`;
  };

  // Same defensive parsing TripAI.js's getItinArray/_parseItinJSON use —
  // strip markdown fences, locate the first JSON bracket, JSON.parse, with a
  // truncate-and-repair fallback if the AI cut off mid-response.
  const parseAiDestinationsJSON = (raw) => {
    let str = raw.replace(/```json\s*/gi, "").replace(/```\s*/g, "");
    const bracketIdx = str.indexOf("[");
    if (bracketIdx !== -1) str = str.substring(bracketIdx);
    try {
      return JSON.parse(str);
    } catch (firstError) {
      const lastClose = str.lastIndexOf("}");
      if (lastClose !== -1) {
        let repaired = str.substring(0, lastClose + 1) + "]";
        try {
          return JSON.parse(repaired);
        } catch (_) {
          // fall through to throw original error
        }
      }
      throw firstError;
    }
  };

  // Resolves {city, country} to real coordinates: the curated citiesByCountry
  // dataset first (fast, free, case-insensitive match), else a live geocode —
  // covers exactly the case the deterministic algorithm can't: a real but
  // uncurated place (e.g. a safari-reserve gateway town) the AI suggests.
  const resolveAiCity = async (city, country) => {
    const cityList = citiesByCountry[country] || [];
    const matched = cityList.find((c) => c.city.toLowerCase() === city.toLowerCase());
    if (matched) return { ...matched, city: matched.city };
    try {
      setKey(process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY);
      const { results } = await geocode(RequestType.ADDRESS, `${city}, ${country}`);
      if (results && results.length > 0) {
        const { lat, lng } = results[0].geometry.location;
        return { city, lat, lon: lng, popularity: 0 };
      }
    } catch (_) {
      // fall through — city dropped below if unresolved
    }
    return null;
  };

  const generateAiDestinations = async () => {
    if (aiSelectedCountries.length === 0) {
      setAiError("Select at least one country.");
      return;
    }
    if (aiEndDateTime <= aiStartDateTime) {
      setAiError("End date/time must be after the start.");
      return;
    }
    const minNights = Math.max(1, Math.round(Number(aiMinNights) || 1));
    const totalDays = totalDaySpan([{ dateStart: aiStartDateTime, dateEnd: aiEndDateTime }]);
    if (totalDays > MAX_TRIP_DAYS) {
      setAiError(`That's a ${totalDays}-day span — maximum is ${MAX_TRIP_DAYS}.`);
      return;
    }

    setAiLoading(true);
    setAiError("");
    try {
      const quest = buildAiPrompt(totalDays, minNights);
      const url = `${API_BASE_URL}/travel_questionsGPT5.6-luna.php?q=${encodeURIComponent(`"${quest}"`)}`;
      const response = await fetch(url).then((r) => r.json());
      const content = response?.choices?.[0]?.message?.content;
      if (!content) {
        setAiError("The AI didn't return a usable response — please try again.");
        setAiLoading(false);
        return;
      }
      const suggestions = parseAiDestinationsJSON(content);
      if (!Array.isArray(suggestions) || suggestions.length === 0) {
        setAiError("The AI didn't suggest any destinations — please try again.");
        setAiLoading(false);
        return;
      }

      const resolved = [];
      for (const s of suggestions) {
        if (!s || !s.city || !s.country) continue;
        const matchedCountry = aiSelectedCountries.find((c) => c.toLowerCase() === String(s.country).toLowerCase()) || s.country;
        const cityData = await resolveAiCity(s.city, matchedCountry);
        if (!cityData) continue;
        resolved.push({ ...cityData, country: matchedCountry, nights: Math.max(1, Math.round(Number(s.nights) || minNights)) });
      }

      if (resolved.length === 0) {
        setAiError("Couldn't resolve any of the AI's suggested destinations to real coordinates — please try again.");
        setAiLoading(false);
        return;
      }

      // Pinned start/end city, if any, forced in the same way the
      // deterministic algorithm does — a flight's arrival/departure point
      // isn't optional just because the AI didn't happen to suggest it.
      const sameCity = (a, b) => !!a && !!b && a.city === b.city && a.country === b.country;
      [aiStartCity, aiEndCity].forEach((pinned) => {
        if (pinned && !resolved.some((p) => sameCity(p, pinned))) resolved.push({ ...pinned, nights: minNights });
      });
      if (aiStartCity) {
        const idx = resolved.findIndex((p) => sameCity(p, aiStartCity));
        if (idx > 0) resolved.unshift(resolved.splice(idx, 1)[0]);
      }
      if (aiEndCity) {
        const idx = resolved.findIndex((p) => sameCity(p, aiEndCity));
        if (idx !== -1 && idx !== resolved.length - 1) resolved.push(resolved.splice(idx, 1)[0]);
      }

      // Rescale nights to sum exactly to totalDays — the AI's total is a
      // best-effort, not guaranteed exact, and the review phase's rechain
      // logic requires the selected total to match the trip span precisely.
      const currentTotal = resolved.reduce((s, c) => s + c.nights, 0);
      if (currentTotal !== totalDays) {
        const diff = totalDays - currentTotal;
        const lastIdx = resolved.length - 1;
        resolved[lastIdx].nights = Math.max(1, resolved[lastIdx].nights + diff);
      }

      const rawCandidates = resolved.map((c, i) => ({
        id: `ai_${i}`,
        city: c.city,
        country: c.country,
        dateStart: aiStartDateTime,
        dateEnd: aiEndDateTime,
        days: c.nights,
        selected: true,
      }));

      setAiTripStartDate(aiStartDateTime);
      setAiTripEndDate(aiEndDateTime);
      setAiCandidates(rechainAiDates(rawCandidates, aiStartDateTime, aiEndDateTime));
      setAiLoading(false);
      setPhase("review");
    } catch (e) {
      console.log("generateAiDestinations error:", e.message);
      setAiError("Something went wrong asking the AI for destinations — please try again.");
      setAiLoading(false);
    }
  };

  // ── Review-phase editing — verbatim from ExploreDestinationsFlow.js ──────
  const applyAiDaysTransfer = (id, partnerId, delta) => {
    setAiCandidates((prev) => {
      const next = prev.map((c) => ({ ...c }));
      const target = next.find((c) => c.id === id);
      const partner = next.find((c) => c.id === partnerId);
      if (!target || !partner) return prev;
      if (delta > 0) {
        if (partner.days - delta < 1) {
          setAiError(`${partner.city} only has ${partner.days} night${partner.days === 1 ? "" : "s"} — can't give up ${delta}.`);
          return prev;
        }
        target.days += delta;
        partner.days -= delta;
      } else {
        target.days += delta;
        partner.days += -delta;
      }
      setAiError("");
      return rechainAiDates(next, aiTripStartDate, aiTripEndDate);
    });
  };

  const applyAiFlexibleDaysChange = (id, delta) => {
    const currentTotal = aiCandidates.filter((c) => c.selected).reduce((s, c) => s + c.days, 0);
    const newTotal = currentTotal + delta;
    if (newTotal > MAX_TRIP_DAYS) {
      setAiError(`The trip would be ${newTotal} days — maximum is ${MAX_TRIP_DAYS}.`);
      return;
    }
    const newTripEnd = aiTripEndDate.plus({ days: delta });
    const next = aiCandidates.map((c) => (c.id === id ? { ...c, days: c.days + delta } : c));
    setAiTripEndDate(newTripEnd);
    setAiCandidates(rechainAiDates(next, aiTripStartDate, newTripEnd));
    setAiError("");
  };

  const setAiCandidateDays = (id, days) => {
    const value = Math.round(Number(days));
    if (!Number.isFinite(value) || value <= 0) {
      removeAiCandidate(id);
      return;
    }
    const target = aiCandidates.find((c) => c.id === id);
    if (!target) return;
    const clamped = Math.max(1, Math.min(21, value));
    const delta = clamped - target.days;
    if (delta === 0) return;

    if (aiDatesFlexible) {
      applyAiFlexibleDaysChange(id, delta);
      return;
    }

    const others = aiCandidates.filter((c) => c.id !== id && c.selected);
    if (others.length === 0) {
      setAiError("No other selected city to take/give days with.");
      return;
    }
    if (others.length === 1) {
      applyAiDaysTransfer(id, others[0].id, delta);
      return;
    }
    const verb = delta > 0 ? "Take" : "Give";
    const prep = delta > 0 ? "from" : "to";
    const amount = Math.abs(delta);
    const items = others.map((c, i) => ({
      label: `${c.city}, ${c.country} (${c.days} night${c.days === 1 ? "" : "s"})`,
      name: c.city,
      value: c.id,
      id: (i + 1).toString(),
    }));
    openMultiSelect(
      items,
      [],
      "uni",
      (selected) => {
        if (!selected || selected.length === 0) return;
        applyAiDaysTransfer(id, selected[0].value, delta);
      },
      `${verb} ${amount} night${amount === 1 ? "" : "s"} ${prep} which city?`
    );
  };

  const toggleAiCandidate = (id) => {
    setAiCandidates((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target) return prev;
      if (target.selected) {
        const freed = target.days;
        const others = prev.filter((c) => c.id !== id && c.selected);
        const receiverId = others.length > 0 ? others[others.length - 1].id : null;
        const next = prev.map((c) => {
          if (c.id === id) return { ...c, selected: false, days: 0 };
          if (c.id === receiverId) return { ...c, days: c.days + freed };
          return c;
        });
        setAiError("");
        return rechainAiDates(next, aiTripStartDate, aiTripEndDate);
      }
      const minNights = Math.max(1, Math.round(Number(aiMinNights) || 1));
      const withSelection = prev.map((c) => (c.id === id ? { ...c, selected: true } : c));
      const rebalanced = rebalanceAiDays(withSelection, id, minNights, 1);
      if (!rebalanced) {
        setAiError("Not enough room to add this city back — every other city is already down to 1 night.");
        return prev;
      }
      setAiError("");
      return rechainAiDates(rebalanced, aiTripStartDate, aiTripEndDate);
    });
  };

  const removeAiCandidate = (id) => {
    setAiCandidates((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target) return prev;
      const freed = target.selected ? target.days : 0;
      const others = prev.filter((c) => c.id !== id && c.selected);
      const receiverId = others.length > 0 ? others[others.length - 1].id : null;
      const next = prev
        .filter((c) => c.id !== id)
        .map((c) => (c.id === receiverId ? { ...c, days: c.days + freed } : c));
      setAiError("");
      return rechainAiDates(next, aiTripStartDate, aiTripEndDate);
    });
  };

  const selectAiSubstituteCity = (id) => {
    const target = aiCandidates.find((c) => c.id === id);
    if (!target) return;
    const alreadyUsed = new Set(aiCandidates.filter((c) => c.id !== id).map((c) => `${c.city}||${c.country}`));
    const choices = (citiesByCountry[target.country] || []).filter((c) => !alreadyUsed.has(`${c.city}||${target.country}`));
    if (choices.length === 0) {
      setAiError(`No other curated cities available for ${target.country}.`);
      return;
    }
    const items = choices.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", (selected) => aiSubstituteCitySelected(id, selected, choices), `Swap ${target.city} for…`);
  };

  const aiSubstituteCitySelected = (id, selected, choices) => {
    if (!selected || selected.length === 0) return;
    const chosen = choices.find((c) => c.city === selected[0].value);
    if (!chosen) return;
    setAiCandidates((prev) =>
      prev.map((c) => (c.id === id ? { ...c, city: chosen.city, lat: chosen.lat, lon: chosen.lon, popularity: chosen.popularity } : c))
    );
    setAiError("");
  };

  const openAiAddCity = () => {
    const items = countries
      .filter((c) => Array.isArray(citiesByCountry[c]) && citiesByCountry[c].length > 0)
      .sort()
      .map((c, i) => ({ label: c, name: c, value: c, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", aiAddCityCountrySelected, "Add a City — Pick a Country");
  };

  const aiAddCityCountrySelected = (selected) => {
    if (!selected || selected.length === 0) return;
    const country = selected[0].value;
    const alreadyUsed = new Set(aiCandidates.map((c) => `${c.city}||${c.country}`));
    const choices = (citiesByCountry[country] || []).filter((c) => !alreadyUsed.has(`${c.city}||${country}`));
    if (choices.length === 0) {
      setAiError(`No other curated cities available for ${country}.`);
      return;
    }
    const items = choices.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", (sel) => aiAddCityCitySelected(sel, choices, country), `Add a City in ${country}`);
  };

  const aiAddCityCitySelected = (selected, choices, country) => {
    if (!selected || selected.length === 0) return;
    const chosen = choices.find((c) => c.city === selected[0].value);
    if (!chosen) return;

    const minNights = Math.max(1, Math.round(Number(aiMinNights) || 1));
    const newId = `ai_added_${Date.now()}`;
    const newCandidate = { id: newId, city: chosen.city, country, lat: chosen.lat, lon: chosen.lon, popularity: chosen.popularity, days: 0, selected: true };

    if (aiDatesFlexible) {
      const currentTotal = aiCandidates.filter((c) => c.selected).reduce((s, c) => s + c.days, 0);
      const newTotal = currentTotal + minNights;
      if (newTotal > MAX_TRIP_DAYS) {
        setAiError(`The trip would be ${newTotal} days — maximum is ${MAX_TRIP_DAYS}.`);
        return;
      }
      const newTripEnd = aiTripEndDate.plus({ days: minNights });
      const withNew = [...aiCandidates, { ...newCandidate, days: minNights }];
      setAiTripEndDate(newTripEnd);
      setAiCandidates(rechainAiDates(withNew, aiTripStartDate, newTripEnd));
      setAiError("");
      return;
    }

    const next = [...aiCandidates, newCandidate];
    const rebalanced = rebalanceAiDays(next, newId, minNights, 1);
    if (!rebalanced) {
      setAiError("Not enough room to add this city — every other city is already down to 1 night.");
      return;
    }
    setAiCandidates(rechainAiDates(rebalanced, aiTripStartDate, aiTripEndDate));
    setAiError("");
  };

  const moveAiCandidate = (id, direction) => {
    setAiCandidates((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const swapWith = idx + direction;
      if (idx === -1 || swapWith < 0 || swapWith >= prev.length) return prev;
      const next = prev.slice();
      const tmp = next[idx];
      next[idx] = next[swapWith];
      next[swapWith] = tmp;
      return rechainAiDates(next, aiTripStartDate, aiTripEndDate);
    });
  };

  const applyAiDestinations = () => {
    const selected = aiCandidates.filter((c) => c.selected);
    if (selected.length === 0) {
      setAiError("Select at least one destination.");
      return;
    }
    const overlap = overlapError(selected);
    if (overlap) {
      setAiError(overlap);
      return;
    }
    const lengthError = tripLengthError(selected);
    if (lengthError) {
      setAiError(lengthError);
      return;
    }
    onApply(selected.map(({ id, city, country, dateStart, dateEnd }) => ({ id, city, country, dateStart, dateEnd })));
  };

  return (
    <>
      {showPreferences && (
        <PreferencesScreen onHide={() => setShowPreferences(false)} />
      )}

      {!showPreferences && multiSelect && (
        <MultiSelectModal
          show={true}
          onHide={() => setMultiSelect(null)}
          MultiselectItems={multiSelect.items}
          MultiselectSelectedItems={multiSelect.selectedItems}
          MultiselectMode={multiSelect.mode}
          MultiselectCallback={(selected) => { const cb = multiSelect.callback; setMultiSelect(null); cb(selected); }}
          MultiselectTitle={multiSelect.title}
          MultiselectSearchText=""
        />
      )}

      {!showPreferences && !multiSelect && phase === "setup" && (
        <div className="tai-paste-overlay" onClick={closeAll}>
          <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
            <p className="tai-paste-title">Destinations from AI</p>
            <p className="tai-paste-hint">
              Pick one or more countries, a total trip length, and any preferences — the AI will
              suggest cities/towns (including smaller ones suited to your trip type), an order, and
              how many nights to spend in each.
            </p>
            {aiError !== "" && <p className="tai-paste-error">{aiError}</p>}
            <div className="tai-row-item" onClick={selectAiCountries}>
              <span className="tai-row-key">Countries</span>
              <span className="tai-row-value">
                {aiSelectedCountries.length === 0 ? "Select…" : aiSelectedCountries.join(", ")}
              </span>
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-date-row">
              <span className="tai-row-key">Arrival</span>
              <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                selected={Common.JSDateFromLuxonDate(aiStartDateTime)}
                // Only bumps the departure date when the existing one is now
                // invalid (on/before the new arrival) — e.g. picking an
                // arrival further out than the old departure. Leaves an
                // already-set, still-valid longer trip alone, so nudging the
                // arrival time doesn't silently shrink a 10-day trip back to
                // 1 day. Added 2026-08-28.
                onChange={(date) => {
                  const newStart = Common.LuxonDateFromJSDate(date);
                  setAiStartDateTime(newStart);
                  if (aiEndDateTime <= newStart) {
                    setAiEndDateTime(newStart.plus({ days: 1 }).set({ hour: dayEndHour, minute: 0 }));
                  }
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={5}
                timeCaption="time"
                dateFormat="MMM d, yyyy HH:mm"
                withPortal
              />
            </div>
            <div className="tai-date-row">
              <span className="tai-row-key">Departure</span>
              <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                selected={Common.JSDateFromLuxonDate(aiEndDateTime)}
                onChange={(date) => setAiEndDateTime(Common.LuxonDateFromJSDate(date))}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={5}
                timeCaption="time"
                dateFormat="MMM d, yyyy HH:mm"
                withPortal
              />
            </div>
            <div className="tai-input-row">
              <span className="tai-input-label">Suggested Nights per City</span>
              <input
                className="tai-input-field"
                type="number"
                min="1"
                max="21"
                value={aiMinNights}
                onChange={(e) => setAiMinNights(e.target.value)}
              />
            </div>
            <label className="tai-explore-flex-toggle">
              <input
                type="checkbox"
                checked={aiDatesFlexible}
                onChange={(e) => setAiDatesFlexible(e.target.checked)}
              />
              <span>
                Dates are flexible — adding a night to a city lengthens the trip (and removing one
                shortens it) instead of taking nights from another city
              </span>
            </label>
            <div className="tai-row-item" onClick={selectAiStartCity}>
              <span className="tai-row-key">Starting City</span>
              <span className="tai-row-value">
                {aiStartCity ? `${aiStartCity.city}, ${aiStartCity.country}` : "Any"}
              </span>
              {aiStartCity && (
                <button className="tai-explore-clear-btn" onClick={(e) => { e.stopPropagation(); setAiStartCity(null); }}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              )}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectAiEndCity}>
              <span className="tai-row-key">Ending City</span>
              <span className="tai-row-value">
                {aiEndCity ? `${aiEndCity.city}, ${aiEndCity.country}` : "Any"}
              </span>
              {aiEndCity && (
                <button className="tai-explore-clear-btn" onClick={(e) => { e.stopPropagation(); setAiEndCity(null); }}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              )}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>

            <p className="tai-paste-title" style={{ marginTop: 12 }}>Preferences</p>
            <p className="tai-paste-hint">
              {(() => {
                const p = window.guide_obj?.aiPreferences;
                const parts = p && [p.TripType, p.travelingWith, p.focusOn, p.Activities, p.selectedBudget, p.selectedPace, p.selectedTransportation, p.selectedAccessibility].filter(Boolean);
                return parts && parts.length > 0 ? parts.join(" · ") : "Not set — using defaults";
              })()}
            </p>
            <button className="tai-paste-dest-btn" onClick={() => setShowPreferences(true)}>
              <FontAwesomeIcon icon={solid("sliders")} />
              Edit Preferences
            </button>

            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={closeAll}>Cancel</button>
              <button className="tai-primary-btn" onClick={generateAiDestinations} disabled={aiLoading}>
                {aiLoading ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!multiSelect && phase === "review" && (
        <div className="tai-paste-overlay" onClick={closeAll}>
          <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
            <p className="tai-paste-title">Destinations from AI</p>
            <p className="tai-paste-hint">
              AI-suggested cities. Uncheck any you don't want, or adjust nights per city.
            </p>
            {aiError !== "" && <p className="tai-paste-error">{aiError}</p>}
            <button className="tai-paste-dest-btn" style={{ marginBottom: 8, flex: "0 0 auto" }} onClick={openAiAddCity}>
              <FontAwesomeIcon icon={solid("circle-plus")} />
              Add City
            </button>
            <div className="tai-schedule-candidates">
              {aiCandidates.map((c, idx) => (
                <div key={c.id} className="tai-schedule-candidate-row">
                  <div className="tai-explore-reorder-col">
                    <button
                      className="tai-explore-reorder-btn"
                      disabled={idx === 0}
                      onClick={() => moveAiCandidate(c.id, -1)}
                      aria-label={`Move ${c.city} earlier`}
                    >
                      <FontAwesomeIcon icon={solid("chevron-up")} />
                    </button>
                    <button
                      className="tai-explore-reorder-btn"
                      disabled={idx === aiCandidates.length - 1}
                      onClick={() => moveAiCandidate(c.id, 1)}
                      aria-label={`Move ${c.city} later`}
                    >
                      <FontAwesomeIcon icon={solid("chevron-down")} />
                    </button>
                  </div>
                  <input type="checkbox" checked={c.selected} onChange={() => toggleAiCandidate(c.id)} />
                  <span className="tai-schedule-candidate-label">{c.city}, {c.country}</span>
                  <input
                    className="tai-explore-days-input"
                    type="number"
                    min="1"
                    max="21"
                    value={c.days}
                    onChange={(e) => setAiCandidateDays(c.id, e.target.value)}
                  />
                  <span className="tai-schedule-candidate-dates tai-schedule-candidate-dates--compact">
                    {c.dateStart.toFormat("LLL d")} – {c.dateStart.hasSame(c.dateEnd, "month") ? c.dateEnd.toFormat("d") : c.dateEnd.toFormat("LLL d")}
                  </span>
                  <button
                    className="tai-explore-icon-btn"
                    onClick={() => selectAiSubstituteCity(c.id)}
                    aria-label={`Swap ${c.city} for a different city`}
                    title="Swap city"
                  >
                    <FontAwesomeIcon icon={solid("arrows-rotate")} />
                  </button>
                  <button
                    className="tai-explore-icon-btn tai-explore-icon-btn-danger"
                    onClick={() => removeAiCandidate(c.id)}
                    aria-label={`Remove ${c.city}`}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={solid("trash")} />
                  </button>
                </div>
              ))}
            </div>
            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={closeAll}>Cancel</button>
              <button
                className="tai-cancel-btn"
                onClick={() => {
                  setPhase("setup");
                  setAiCandidates([]);
                  setAiTripStartDate(null);
                  setAiTripEndDate(null);
                  setAiError("");
                }}
              >
                <FontAwesomeIcon icon={solid("arrow-left")} />
                Back
              </button>
              <button className="tai-primary-btn" onClick={applyAiDestinations}>Use Selected</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIDestinationsFlow;
